import React, { Fragment } from "react";
import PropTypes from "prop-types";
import styled, { css } from "styled-components";

import colors from "core/colors";

const createHelpers = (width, height, css) => {
    // somehow sizes is ending up in markup, even if it is not a valid svg attribute
    // until we have a better solution, just render it empty, instead to '[Object object]'
    const sanitizeSizes = sizes =>
        Object.defineProperty(sizes, "toString", {
            value: () => "",
            enumerable: false
        });

    const getDimensions = (size, sizes) => {
        if (
            size &&
            typeof size.width === "number" &&
            typeof size.height === "number"
        ) {
            return size;
        }

        return size && sizes[size] ? sizes[size] : { width, height };
    };

    const getCss = (size, sizes, fillColor, fillColorRule, noStyles) => {
        if (noStyles) {
            return "";
        }

        const dimensions = getDimensions(size, sizes);
        const fillRule =
            fillColor && fillColorRule
                ? `${fillColorRule}{ fill: ${fillColor}; }`
                : "";
        return css`
            width: ${dimensions.width}px;
            height: ${dimensions.height}px;
            ${fillRule}
        `;
    };

    const propsToCss = ({ size, sizes, fillColor, fillColorRule, noStyles }) =>
        getCss(size, sizes, fillColor, fillColorRule, noStyles);

    return {
        getCss,
        getDimensions,
        propsToCss,
        sanitizeSizes
    };
};

const width = "22";
const height = "22";
const viewBox = "0 0 22 22";

const { getDimensions, getCss, propsToCss, sanitizeSizes } = createHelpers(
    width,
    height,
    css
);

const sizes = sanitizeSizes({
    xs: { width: 14, height: 14 },
    sm: { width: 16, height: 16 },
    md: { width: 20, height: 20 },
    lg: { width: 24, height: 24 },
    xl: { width: 40, height: 40 }
});

const Image = styled.svg`
    ${propsToCss}
`;

const children = (
    <Fragment>
        <path
            id="private eye"
            fillRule="evenodd"
            clipRule="evenodd"
            d="M4.46414 18.277C4.6594 18.4722 4.97598 18.4722 5.17124 18.277L18.7242 4.72414C18.9195 4.52888 18.9195 4.21229 18.7242 4.01703L17.8109 3.10366C17.6156 2.9084 17.299 2.9084 17.1037 3.10366L14.0051 6.20225C13.752 6.12585 13.4955 6.05948 13.2356 6.00414C12.1237 5.76764 11.0036 5.75756 9.88707 5.95739C8.87048 6.13889 7.89056 6.51748 6.9739 6.98589C5.5384 7.71831 4.22757 8.73123 3.08448 9.86423C2.87456 10.0741 2.66281 10.2877 2.47123 10.516C2.21548 10.8194 2.21548 11.1787 2.47123 11.4821C3.05606 12.1751 3.75182 12.7911 4.45948 13.3558C4.90428 13.7102 5.36791 14.0476 5.8493 14.358L3.55079 16.6565C3.35553 16.8518 3.35553 17.1684 3.55079 17.3636L4.46414 18.277ZM8.0979 12.1094L12.5573 7.65013C12.208 7.53439 11.8346 7.47173 11.4463 7.47173C9.49932 7.47173 7.9199 9.05114 7.9199 10.9991C7.9199 11.3871 7.98241 11.7604 8.0979 12.1094ZM9.64965 15.994C9.39279 15.9393 9.1391 15.8739 8.88869 15.7986L10.3377 14.3496C10.6863 14.4649 11.059 14.5273 11.4463 14.5273C13.3961 14.5273 14.9736 12.9479 14.9736 10.9991C14.9736 10.6121 14.9113 10.2396 14.7961 9.8911L17.0425 7.64477C17.5212 7.95408 17.9824 8.29021 18.4249 8.64323C19.1326 9.20698 19.8283 9.82389 20.4132 10.5169C20.5158 10.6196 20.5827 10.7516 20.6056 10.8936V10.8964C20.6061 10.8993 20.6067 10.9021 20.6073 10.9049C20.6077 10.9068 20.6081 10.9086 20.6085 10.9106C20.6088 10.9122 20.6091 10.9139 20.6093 10.9156V10.9157C20.6093 10.9203 20.6093 10.9257 20.6102 10.9321C20.6111 10.9349 20.6111 10.9395 20.6111 10.9431V10.9431C20.6111 10.9482 20.612 10.9536 20.6129 10.9589L20.6139 10.9661L20.6148 10.9633V10.9963V11.0311C20.6148 11.0354 20.6141 11.0394 20.6134 11.0433L20.6134 11.0433C20.6127 11.047 20.6121 11.0505 20.6121 11.0541V11.066C20.6102 11.0706 20.6093 11.0761 20.6093 11.0816C20.6075 11.088 20.6066 11.0953 20.6066 11.1008C20.6066 11.1026 20.6056 11.1036 20.6056 11.1036C20.5827 11.2466 20.5158 11.3786 20.4132 11.4812C20.2225 11.7095 20.0117 11.9231 19.7999 12.133C18.6577 13.2669 17.346 14.2771 15.9114 15.0113C14.9947 15.4806 14.0139 15.8574 12.9973 16.0389C11.8799 16.2396 10.7606 16.2305 9.64965 15.994Z"
            fill={colors.secondary.orca}
        />
    </Fragment>
);

const defaultProps = {
    children,
    viewBox,
    fillColor: null,
    fillColorRule: "&&& path, &&& use, &&& g, &&& circle",
    sizes,
    size: null
};

Image.propTypes /* remove-proptypes */ = {
    fillColor: PropTypes.string,
    fillColorRule: PropTypes.string,
    viewBox: PropTypes.string.isRequired,
    children: PropTypes.node.isRequired,
    size: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.shape({
            height: PropTypes.number.isRequired,
            width: PropTypes.number.isRequired
        })
    ]),
    sizes: PropTypes.shape({
        height: PropTypes.number,
        width: PropTypes.number
    })
};

export default Object.assign(Image, {
    getDimensions,
    getCss,
    defaultProps,
    displayName: "Eye Blind"
});
